import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useSnackbar } from "notistack";
import { getTrackerDetailApi } from "src/DAL/tracker/tracker";
import { normalizeTracker } from "src/pages/Trackers/trackerStorage";
import { getSubmissionPersonKey, loadSubmissionsFromApi } from "src/pages/Trackers/submissionStorage";
import StaffCheckInDialog from "src/pages/Trackers/StaffCheckInDialog";
import { TextLink } from "./cwUi";

/**
 * STAFF-SUBMIT (3 Nov, client video 18:08–19:38) — "Submit check-in for
 * client" on the review page and the client workspace. Opens the SAME
 * dialog as Trackers → journey → People → "Check-in for member"
 * (StaffCheckInDialog, same API and the same open-date rules). The journey
 * and the person's rows load only when the coach clicks.
 */

const sameText = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

/**
 * ACTIONS-MENU (3 Nov): `hideTrigger` + a ref ({ open }) lets a "⋮" menu item
 * open the same dialog (the dialog stays here, outside the menu).
 */
const SubmitForClient = forwardRef(function SubmitForClient(
  { trackerId, person, onSaved, label = "Submit check-in for client", sx, testId, hideTrigger = false },
  ref
) {
  const { enqueueSnackbar } = useSnackbar();
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState(null); // { tracker, rows }

  const open = async () => {
    if (!trackerId || !person) return;
    setBusy(true);
    const [detail, list] = await Promise.all([
      getTrackerDetailApi(trackerId).catch(() => null),
      loadSubmissionsFromApi(trackerId, { limit: 500, search: person.email || "" }),
    ]);
    setBusy(false);
    if (detail?.code !== 200 || !detail.tracker) {
      enqueueSnackbar(detail?.message || "Could not load this journey", { variant: "error" });
      return;
    }
    const keys = [person.personKey, person.email].filter(Boolean);
    const rows = (list.rows || []).filter((row) => {
      const key = getSubmissionPersonKey(row);
      return (
        keys.some((k) => sameText(k, key) || sameText(k, row.email) || sameText(k, row.personKey)) ||
        (person.memberId && String(row.memberId || "") === String(person.memberId))
      );
    });
    setState({ tracker: normalizeTracker(detail.tracker), rows });
  };

  useImperativeHandle(ref, () => ({ open, busy }));

  const dialogPerson = person
    ? {
        ...person,
        key: person.personKey || person.email || person.memberId,
        personKey: person.personKey || person.email || "",
      }
    : null;

  return (
    <>
      {hideTrigger ? null : (
        <TextLink onClick={open} disabled={busy || !trackerId} sx={sx} data-testid={testId || "submit-for-client"}>
          {busy ? "Loading…" : label}
        </TextLink>
      )}
      {state ? (
        <StaffCheckInDialog
          open
          tracker={state.tracker}
          person={dialogPerson}
          personSubmissions={state.rows}
          onClose={() => setState(null)}
          onSaved={(submission) => {
            setState(null);
            if (onSaved) onSaved(submission);
          }}
        />
      ) : null}
    </>
  );
});

export default SubmitForClient;
